/**
 * WHICH RECORD THIS BROWSER IS LOOKING AT, read off the address bar.
 *
 * The org is the subdomain (docs/PLATFORMS.md: only ORG names one), so
 * the host answers a question nothing else can at load time -- before
 * any token exists, before any request goes out. `{org}.polywog.io` is
 * a record's platform; the bare apex is not a record at all.
 *
 * That distinction is what the apex screen needs. polywog.io cannot
 * sign anybody in: Identity Platform tenants are per-org user pools
 * (the same email in two orgs is two distinct users), so there is no
 * tenant to authenticate against until an org is named. The apex asks
 * which one and sends you there; the sign-in happens on the subdomain,
 * inside the tenant it selects.
 *
 * Label counting, not a configured base domain, on purpose: a record's
 * host has one more label than the apex, and that is true of
 * polywog.io, of a preview domain, and of whatever the fleet is called
 * next. A base domain in the bundle would be a fourth copy of a name
 * the naming law already owns.
 */
import { isOrgName } from '../../api';

/** Labels in the bare apex (`polywog.io`). A record adds one. */
const APEX_LABELS = 2;

/**
 * Hosts that are a developer's machine rather than a platform. They
 * have no subdomain and must never dispatch -- `make up` would bounce
 * the browser off the local stack and into the real fleet.
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);

/**
 * The org this host serves, or null when the host is not a record's.
 *
 * Null means one of three things, all of which want the same answer
 * from the caller (do not assume an org): the bare apex, a local dev
 * host, or a leading label whose SHAPE is not an org name's.
 *
 * Shape only. The reserved names (`www`, `api`, `app`...) are policy
 * the server owns, and core/platform/org.py keeps them a validator rather
 * than a constraint on purpose -- "this is policy, not shape; the list
 * grows without the published contract changing". So the list does not
 * publish, and a browser carrying its own copy would be a copy that
 * silently falls behind. `www.polywog.io` is a HOSTING concern: point
 * it at the apex site and this code never sees it.
 */
export function orgFromHost(hostname: string): string | null {
  if (LOCAL_HOSTS.has(hostname)) return null;
  const labels = hostname.split('.');
  if (labels.length <= APEX_LABELS) return null;
  const candidate = labels[0];
  return isOrgName(candidate) ? candidate : null;
}

/**
 * Whether this browser is at the fleet's front door rather than inside
 * a record.
 */
export function isApexHost(hostname: string): boolean {
  return !LOCAL_HOSTS.has(hostname) && hostname.split('.').length <= APEX_LABELS;
}

/**
 * Where naming an org sends you: the same host with the org in front.
 *
 * Built from the CURRENT host so the apex never needs to be told what
 * domain it is serving -- a preview deploy dispatches within itself
 * rather than throwing people at production.
 */
export function recordUrl(org: string, location: Location, email = ''): string {
  const base = `${location.protocol}//${org}.${location.host}`;
  // THE ADDRESS RIDES ALONG, and it is what makes the record able to
  // sign somebody in without asking a second time. They typed it at the
  // front door; the record hands it to Google as the account to use, so
  // nobody is ever presented with a chooser.
  //
  // In the FRAGMENT, not a query: fragments are not sent to the server,
  // do not reach access logs, and are not carried on a Referer. This is
  // the person's own address in their own browser and it authorizes
  // nothing -- the seat is held for an identity only Google can attest
  // -- but there is no reason to write it into anybody's logs.
  return email ? `${base}#email=${encodeURIComponent(email)}` : base;
}

/**
 * The address the front door sent along, or null when it sent none.
 *
 * NO ADDRESS MEANS THEY DID NOT COME THROUGH THE FRONT DOOR. A record
 * signs in the person the apex identified; somebody who typed the
 * subdomain directly has told us nothing, and belongs at polywog.io.
 *
 * @param location - The browser's current location.
 * @returns The address, or null.
 */
export function invitedEmail(location: Location): string | null {
  const match = /(?:^|&)email=([^&]+)/.exec(location.hash.replace(/^#/, ''));
  return match ? decodeURIComponent(match[1]) : null;
}


/**
 * The org an email address belongs to, or null when it names none.
 *
 * AN ORG HOSTS ITS OWN EMAIL DOMAIN (user ruling): that is what makes
 * this derivable at all, and it is why the apex can ask for an address
 * instead of a workspace name. `you@reliant-eng.com` is reliant-eng.
 *
 * DERIVED, NEVER LOOKED UP. The whole computation happens in the
 * browser with no request, so there is nothing to enumerate -- an
 * unauthenticated endpoint answering "does this address have an
 * account, and at which firm" would hand over the customer list of a
 * product whose customers are named engineering firms. The domain
 * already carries the answer.
 *
 * The label taken is the one BEFORE the public suffix, so a mail
 * subdomain does not fool it (`you@mail.reliant-eng.com` is still
 * reliant-eng). A multi-part suffix would (`acme.co.uk` reads as
 * `co`); the address is choosing which subdomain to sign in on, and
 * the wrong one refuses at its own door.
 */
export function orgFromEmail(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const labels = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .split('.')
    .filter(Boolean);
  if (labels.length < 2) return null;
  const candidate = labels[labels.length - 2];
  return isOrgName(candidate) ? candidate : null;
}

/**
 * The front door, from inside a record.
 *
 * A record's host is the apex with one label in front, so dropping
 * that label walks back out -- no configured base domain, for the same
 * reason orgFromHost counts labels rather than matching one.
 *
 * @param location - The browser's current location.
 * @returns The apex URL this subdomain sits under.
 */
export function apexUrl(location: Location): string {
  const labels = location.host.split('.');
  return `${location.protocol}//${labels.slice(1).join('.')}`;
}

/**
 * The path an invite link lands on at the apex: `/join/{org}`.
 *
 * The org rides the PATH rather than a query string or a token,
 * because it is not a secret and pretending otherwise would be
 * theatre. Knowing an org's name grants nothing -- the subdomain is
 * public, and the seat is held for a VERIFIED email that only the
 * identity provider can attest. The link's whole job is to save the
 * invited person from having to be told what to type.
 */
const JOIN_PREFIX = '/join/';

/**
 * The org an invite link names, or null when this is not one.
 *
 * Shape-checked like every other org read off the address bar: a
 * malformed path prefills nothing rather than sending somebody to a
 * subdomain that cannot exist.
 */
export function invitedOrgFromPath(pathname: string): string | null {
  if (!pathname.startsWith(JOIN_PREFIX)) return null;
  const candidate = pathname.slice(JOIN_PREFIX.length).replace(/\/+$/, '');
  return isOrgName(candidate) ? candidate : null;
}

/**
 * The link an operator sends an invited person: the apex, carrying the
 * org they were invited to.
 */
export function invitationUrl(org: string, apex: string): string {
  return `https://${apex}${JOIN_PREFIX}${org}`;
}
